import { JsonSchemaModel } from '../Models/JsonSchemaModel';
import { ExpressionModel } from '../Models/ExpressionModel';
import { ObjectPropertyType } from '../Models/ObjectPropertyType';
import { getJsonSchemaFromProperty } from './EntityMetadataModelConverter';

type Projection = { sourcePath: string; targetPath: string; kind: string };
type Variant = { value: string; schema: JsonSchemaModel; projections?: Projection[] };
type Rule = { targetPath: string; sourceRef: string; discriminator: string; variants: Variant[] };
type InferenceSchema = JsonSchemaModel & { 'x-dependentDictionaries'?: Rule[]; 'x-knownLength'?: number };
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

function expandConst(schema: JsonSchemaModel): JsonSchemaModel {
	if (schema.const && typeof schema.const === 'object' && !Array.isArray(schema.const)) {
		const entries = Object.entries(schema.const as Record<string, unknown>);
		return { type: 'object', additionalProperties: false, required: entries.map(([key]) => key),
			properties: Object.fromEntries(entries.map(([key, value]) => [key, expandConst({ const: value })])) };
	}
	return schema;
}

function atPath(schema: JsonSchemaModel, path: string): JsonSchemaModel | undefined {
	let current: JsonSchemaModel | undefined = expandConst(schema);
	for (const segment of path.split('.').filter(Boolean)) current = current?.properties?.[segment] ? expandConst(current.properties[segment]) : undefined;
	return current;
}

function specialize(schema: JsonSchemaModel, projection: Projection, keys: string[]): void {
	for (const branches of [schema.anyOf, schema.oneOf, schema.allOf]) {
		for (const branch of branches ?? []) specialize(branch, projection, keys);
	}
	const target = atPath(schema, projection.targetPath);
	if (!target || target.type === 'null') return;
	if (projection.kind === 'EnumKeys') target.enum = keys;
	else if (target.additionalProperties && typeof target.additionalProperties === 'object') {
		const item = target.additionalProperties;
		target.properties = Object.fromEntries(keys.map(key => [key, clone(item)]));
		target.required = keys;
		target.additionalProperties = false;
	}
}

/** Pure interpretation of backend-authored rules. No provider calls or expression execution. */
export function resolveDependentDictionarySchema(schema: JsonSchemaModel, sourceSchema: (sourceRef: string) => JsonSchemaModel | undefined): JsonSchemaModel {
	const root = clone(schema) as InferenceSchema;
	const rules = root['x-dependentDictionaries'] ?? [];
	delete root['x-dependentDictionaries'];
	for (const rule of rules) {
		const source = sourceSchema(rule.sourceRef);
		const target = atPath(root, rule.targetPath);
		if (!source || !target) continue;
		const known = expandConst(source);
		if (!known.properties) continue;
		target.properties = Object.fromEntries(Object.entries(known.properties).map(([key, question]) => {
			const definition = expandConst(question);
			const discriminator = atPath(definition, rule.discriminator);
			const value = discriminator?.const ?? (discriminator?.enum?.length === 1 ? discriminator.enum[0] : undefined);
			const variant = rule.variants.find(candidate => candidate.value === value);
			const answer = clone(variant?.schema ?? (typeof target.additionalProperties === 'object' ? target.additionalProperties : {}));
			for (const projection of variant?.projections ?? []) {
				const from = atPath(definition, projection.sourcePath) as InferenceSchema | undefined;
				if (projection.kind !== 'ArrayIndices' && (from?.additionalProperties !== false
					|| Object.keys(from?.properties ?? {}).some(key => !from?.required?.includes(key)))) continue;
				const keys = projection.kind === 'ArrayIndices'
					? Array.isArray(from?.const) ? from!.const.map((_: unknown, index: number) => String(index))
						: from?.['x-knownLength'] !== undefined ? Array.from({ length: from['x-knownLength'] }, (_, index) => String(index)) : undefined
					: from?.properties ? Object.keys(from.properties) : undefined;
				if (keys) specialize(answer, projection, keys);
			}
			return [key, answer];
		}));
		target.required = known.required ?? [];
		if (known.additionalProperties === false) target.additionalProperties = false;
	}
	return root;
}

export function getDependentSourceSchema(value: unknown, infer: (expression: ExpressionModel) => ObjectPropertyType | undefined): JsonSchemaModel | undefined {
	if (!value || typeof value !== 'object') return undefined;
	const expression = value as ExpressionModel;
	if (expression.Type === 'Const') return { const: expression.Value };
	if (expression.Type === 'Object' && expression.MapIf == null) {
		const entries = Object.entries(expression.Properties ?? {});
		return { type: 'object', additionalProperties: false, required: entries.map(([key]) => key),
			properties: Object.fromEntries(entries.map(([key, item]) => [key, getDependentSourceSchema(item, infer) ?? {}])) };
	}
	if (expression.Type === 'Collection') return { type: 'array', 'x-knownLength': expression.Items?.length ?? 0 } as InferenceSchema;
	const type = infer(expression);
	return type ? getJsonSchemaFromProperty(type) : undefined;
}

export function resolveDependentSourceRef(data: Record<string, any>, reference: string,
	infer: (expression: ExpressionModel) => ObjectPropertyType | undefined): JsonSchemaModel | undefined {
	const [root, ...path] = reference.split('.');
	const schema = getDependentSourceSchema(data[root], infer);
	return schema ? atPath(schema, path.join('.')) : undefined;
}
